// src/components/SectionErrorBoundary.js
import React from "react";
import { captureException } from "../lib/errorTracking";
import { logError } from "../lib/logger";
import { getUserFriendlyMessage, getErrorGuidance } from "../lib/errorUtils";
import "./ErrorBoundary.css";

/**
 * Section-specific Error Boundary
 * Wraps individual sections to prevent one section's error from crashing the entire app
 */
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      userMessage: null,
      guidance: null,
    };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { sectionName } = this.props;

    // Log error with section context
    logError(`Error in ${sectionName || "section"}`, error, {
      componentStack: errorInfo.componentStack,
      errorInfo,
      section: sectionName,
    });

    // Get user-friendly message and guidance
    const userMessage = getUserFriendlyMessage(
      error,
      `Something went wrong in the ${sectionName || "section"}.`
    );
    const guidance = getErrorGuidance(error);

    this.setState({
      error,
      errorInfo,
      userMessage,
      guidance,
    });

    // Send error to error tracking service with section context
    captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
      tags: {
        errorBoundary: true,
        section: sectionName || "unknown",
        type: "section",
      },
      extra: {
        errorInfo,
        userMessage,
        guidance,
        sectionName,
      },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      userMessage: null,
      guidance: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const { sectionName, fallback } = this.props;

      // Allow custom fallback UI
      if (fallback) {
        return fallback(this.state, this.handleReset);
      }

      // Default section-specific error UI
      return (
        <div className="error-boundary section-error-boundary" role="alert">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">
              {sectionName ? `Error in ${sectionName}` : "Section Error"}
            </h2>
            <p className="error-boundary-message">
              {this.state.userMessage ||
                `We're sorry, but something went wrong in the ${sectionName || "section"}. The rest of the app should still work.`}
            </p>

            {this.state.guidance && (
              <div className="error-boundary-guidance">
                <p className="error-boundary-guidance-title">
                  <strong>What you can do:</strong>
                </p>
                <ul className="error-boundary-guidance-steps">
                  {this.state.guidance.steps?.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {import.meta.env.DEV && this.state.error && (
              <details className="error-boundary-details">
                <summary className="error-boundary-summary">
                  Error Details (Development Only)
                </summary>
                <div className="error-boundary-stack">
                  <pre className="error-boundary-pre">
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <button onClick={this.handleReset} className="error-boundary-button">
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="error-boundary-button error-boundary-button--secondary"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SectionErrorBoundary;
